/**
 * Beautiful preset themes for tasking.tech
 * Designed with the frontend-design skill: distinctive fonts, cohesive palettes,
 * atmospheric depth, and bold aesthetic direction.
 *
 * Each theme overrides the CSS variables defined in base.css.
 * Only color/shadow/font variables are overridden; spacing/radius inherit from base.
 */

export interface CustomThemePreset {
  id: string;
  name: string;
  description: string;
  /** Swatch colors shown in the dropdown: [bg, primary, accent] */
  swatches: [string, string, string];
  /** Whether this is a dark or light base theme */
  base: 'dark' | 'light';
  /** CSS variable overrides applied to :root */
  variables: Record<string, string>;
}

export const THEME_PRESETS: CustomThemePreset[] = [
  // ── 1. Obsidian Ember ──────────────────────────────────────────────────
  {
    id: 'obsidian-ember',
    name: 'Obsidian Ember',
    description: 'Deep volcanic blacks with smoldering amber accents',
    swatches: ['#0d0d0f', '#e8762b', '#ff4d4d'],
    base: 'dark',
    variables: {
      '--background': 'hsl(240 10% 4%)',
      '--foreground': 'hsl(35 15% 90%)',
      '--surface': 'hsl(240 8% 7%)',
      '--surface-foreground': 'hsl(35 10% 93%)',
      '--sidebar': 'hsl(240 8% 6%)',
      '--sidebar-foreground': 'hsl(35 10% 75%)',
      '--card': 'hsl(240 8% 9%)',
      '--card-foreground': 'hsl(35 10% 93%)',
      '--popup': 'hsl(240 8% 11%)',
      '--popup-foreground': 'hsl(35 10% 96%)',
      '--primary': 'hsl(25 82% 54%)',
      '--primary-foreground': 'hsl(240 10% 4%)',
      '--secondary': 'hsl(240 6% 18%)',
      '--secondary-foreground': 'hsl(35 10% 85%)',
      '--accent': 'hsl(0 100% 65%)',
      '--accent-foreground': 'hsl(0 0% 100%)',
      '--muted': 'hsl(240 6% 14%)',
      '--muted-foreground': 'hsl(35 5% 55%)',
      '--border': 'hsl(240 6% 18%)',
      '--error': 'hsl(0 85% 60%)',
      '--warning': 'hsl(35 95% 55%)',
      '--success': 'hsl(145 65% 42%)',
      '--info': 'hsl(200 75% 50%)',
      '--shadow': '0 1px 4px 0 rgba(0,0,0,0.5), 0 1px 2px -1px rgba(0,0,0,0.4)',
      '--shadow-md': '0 4px 12px -2px rgba(0,0,0,0.6), 0 2px 4px -2px rgba(0,0,0,0.5)',
      '--shadow-lg': '0 12px 32px -4px rgba(0,0,0,0.7), 0 4px 8px -4px rgba(0,0,0,0.5)',
      '--shadow-glow-primary': '0 0 24px rgba(232,118,43,0.2), 0 0 48px rgba(232,118,43,0.06)',
      '--shadow-glow-accent': '0 0 24px rgba(255,77,77,0.15), 0 0 48px rgba(255,77,77,0.05)',
    },
  },

  // ── 2. Arctic Frost ────────────────────────────────────────────────────
  {
    id: 'arctic-frost',
    name: 'Arctic Frost',
    description: 'Crystalline whites with glacial blue and ice-mint highlights',
    swatches: ['#f4f7fa', '#2563eb', '#06d6a0'],
    base: 'light',
    variables: {
      '--background': 'hsl(215 30% 97%)',
      '--foreground': 'hsl(215 25% 12%)',
      '--surface': 'hsl(215 25% 94%)',
      '--surface-foreground': 'hsl(215 25% 8%)',
      '--sidebar': 'hsl(215 28% 95%)',
      '--sidebar-foreground': 'hsl(215 20% 25%)',
      '--card': 'hsl(0 0% 100%)',
      '--card-foreground': 'hsl(215 25% 8%)',
      '--popup': 'hsl(0 0% 100%)',
      '--popup-foreground': 'hsl(215 25% 8%)',
      '--primary': 'hsl(220 91% 54%)',
      '--primary-foreground': 'hsl(0 0% 100%)',
      '--secondary': 'hsl(215 20% 88%)',
      '--secondary-foreground': 'hsl(215 25% 15%)',
      '--accent': 'hsl(160 90% 43%)',
      '--accent-foreground': 'hsl(160 10% 5%)',
      '--muted': 'hsl(215 15% 90%)',
      '--muted-foreground': 'hsl(215 10% 42%)',
      '--border': 'hsl(215 18% 85%)',
      '--error': 'hsl(0 72% 51%)',
      '--warning': 'hsl(38 92% 50%)',
      '--success': 'hsl(160 84% 39%)',
      '--info': 'hsl(200 80% 50%)',
      '--shadow': '0 1px 3px 0 rgba(37,99,235,0.06), 0 1px 2px -1px rgba(37,99,235,0.04)',
      '--shadow-md': '0 4px 10px -2px rgba(37,99,235,0.08), 0 2px 4px -2px rgba(37,99,235,0.05)',
      '--shadow-lg': '0 12px 28px -4px rgba(37,99,235,0.1), 0 4px 8px -4px rgba(37,99,235,0.06)',
      '--shadow-glow-primary': '0 0 24px rgba(37,99,235,0.12), 0 0 48px rgba(37,99,235,0.04)',
      '--shadow-glow-accent': '0 0 24px rgba(6,214,160,0.12), 0 0 48px rgba(6,214,160,0.04)',
    },
  },

  // ── 3. Midnight Orchid ─────────────────────────────────────────────────
  {
    id: 'midnight-orchid',
    name: 'Midnight Orchid',
    description: 'Deep indigo canvas with luminous violet and electric pink',
    swatches: ['#0f0a1e', '#a855f7', '#ec4899'],
    base: 'dark',
    variables: {
      '--background': 'hsl(260 40% 6%)',
      '--foreground': 'hsl(270 15% 92%)',
      '--surface': 'hsl(260 35% 9%)',
      '--surface-foreground': 'hsl(270 10% 94%)',
      '--sidebar': 'hsl(260 35% 8%)',
      '--sidebar-foreground': 'hsl(270 10% 75%)',
      '--card': 'hsl(260 30% 12%)',
      '--card-foreground': 'hsl(270 10% 94%)',
      '--popup': 'hsl(260 30% 14%)',
      '--popup-foreground': 'hsl(270 10% 96%)',
      '--primary': 'hsl(270 90% 65%)',
      '--primary-foreground': 'hsl(0 0% 100%)',
      '--secondary': 'hsl(260 20% 20%)',
      '--secondary-foreground': 'hsl(270 10% 88%)',
      '--accent': 'hsl(330 80% 60%)',
      '--accent-foreground': 'hsl(0 0% 100%)',
      '--muted': 'hsl(260 20% 16%)',
      '--muted-foreground': 'hsl(270 8% 55%)',
      '--border': 'hsl(260 18% 22%)',
      '--error': 'hsl(0 80% 60%)',
      '--warning': 'hsl(45 90% 55%)',
      '--success': 'hsl(145 65% 45%)',
      '--info': 'hsl(200 80% 55%)',
      '--shadow': '0 1px 4px 0 rgba(0,0,0,0.5), 0 1px 2px -1px rgba(168,85,247,0.1)',
      '--shadow-md': '0 4px 12px -2px rgba(0,0,0,0.5), 0 2px 4px -2px rgba(168,85,247,0.12)',
      '--shadow-lg': '0 12px 32px -4px rgba(0,0,0,0.6), 0 4px 8px -4px rgba(168,85,247,0.15)',
      '--shadow-glow-primary': '0 0 24px rgba(168,85,247,0.2), 0 0 48px rgba(168,85,247,0.08)',
      '--shadow-glow-accent': '0 0 24px rgba(236,72,153,0.18), 0 0 48px rgba(236,72,153,0.06)',
    },
  },

  // ── 4. Sahara Gold ─────────────────────────────────────────────────────
  {
    id: 'sahara-gold',
    name: 'Sahara Gold',
    description: 'Warm sandstone foundation with burnished gold and terracotta',
    swatches: ['#faf5ee', '#b8860b', '#c0582b'],
    base: 'light',
    variables: {
      '--background': 'hsl(38 40% 96%)',
      '--foreground': 'hsl(30 20% 12%)',
      '--surface': 'hsl(38 35% 92%)',
      '--surface-foreground': 'hsl(30 20% 8%)',
      '--sidebar': 'hsl(38 35% 93%)',
      '--sidebar-foreground': 'hsl(30 15% 25%)',
      '--card': 'hsl(38 30% 98%)',
      '--card-foreground': 'hsl(30 20% 8%)',
      '--popup': 'hsl(38 30% 99%)',
      '--popup-foreground': 'hsl(30 20% 8%)',
      '--primary': 'hsl(42 85% 38%)',
      '--primary-foreground': 'hsl(0 0% 100%)',
      '--secondary': 'hsl(38 20% 85%)',
      '--secondary-foreground': 'hsl(30 15% 18%)',
      '--accent': 'hsl(16 65% 46%)',
      '--accent-foreground': 'hsl(0 0% 100%)',
      '--muted': 'hsl(38 15% 88%)',
      '--muted-foreground': 'hsl(30 10% 42%)',
      '--border': 'hsl(38 18% 80%)',
      '--error': 'hsl(0 72% 50%)',
      '--warning': 'hsl(38 95% 50%)',
      '--success': 'hsl(145 60% 38%)',
      '--info': 'hsl(200 70% 48%)',
      '--shadow': '0 1px 3px 0 rgba(184,134,11,0.06), 0 1px 2px -1px rgba(184,134,11,0.04)',
      '--shadow-md': '0 4px 10px -2px rgba(184,134,11,0.08), 0 2px 4px -2px rgba(184,134,11,0.05)',
      '--shadow-lg': '0 12px 28px -4px rgba(184,134,11,0.1), 0 4px 8px -4px rgba(184,134,11,0.06)',
      '--shadow-glow-primary': '0 0 24px rgba(184,134,11,0.12), 0 0 48px rgba(184,134,11,0.04)',
      '--shadow-glow-accent': '0 0 24px rgba(192,88,43,0.12), 0 0 48px rgba(192,88,43,0.04)',
    },
  },

  // ── 5. Neon Terminus ───────────────────────────────────────────────────
  {
    id: 'neon-terminus',
    name: 'Neon Terminus',
    description: 'Cyberpunk terminal black with electric cyan and hot magenta',
    swatches: ['#050508', '#00ffcc', '#ff0080'],
    base: 'dark',
    variables: {
      '--background': 'hsl(240 20% 2%)',
      '--foreground': 'hsl(165 100% 90%)',
      '--surface': 'hsl(240 15% 5%)',
      '--surface-foreground': 'hsl(165 60% 92%)',
      '--sidebar': 'hsl(240 15% 4%)',
      '--sidebar-foreground': 'hsl(165 40% 70%)',
      '--card': 'hsl(240 12% 8%)',
      '--card-foreground': 'hsl(165 60% 92%)',
      '--popup': 'hsl(240 12% 10%)',
      '--popup-foreground': 'hsl(165 60% 95%)',
      '--primary': 'hsl(165 100% 50%)',
      '--primary-foreground': 'hsl(240 20% 2%)',
      '--secondary': 'hsl(240 10% 16%)',
      '--secondary-foreground': 'hsl(165 40% 85%)',
      '--accent': 'hsl(330 100% 50%)',
      '--accent-foreground': 'hsl(0 0% 100%)',
      '--muted': 'hsl(240 10% 12%)',
      '--muted-foreground': 'hsl(165 20% 50%)',
      '--border': 'hsl(240 10% 18%)',
      '--error': 'hsl(0 100% 60%)',
      '--warning': 'hsl(50 100% 55%)',
      '--success': 'hsl(165 100% 45%)',
      '--info': 'hsl(200 100% 55%)',
      '--shadow': '0 1px 4px 0 rgba(0,255,204,0.06), 0 1px 2px -1px rgba(0,0,0,0.5)',
      '--shadow-md': '0 4px 12px -2px rgba(0,255,204,0.08), 0 2px 4px -2px rgba(0,0,0,0.5)',
      '--shadow-lg': '0 12px 32px -4px rgba(0,255,204,0.1), 0 4px 8px -4px rgba(0,0,0,0.5)',
      '--shadow-glow-primary': '0 0 24px rgba(0,255,204,0.25), 0 0 60px rgba(0,255,204,0.08)',
      '--shadow-glow-accent': '0 0 24px rgba(255,0,128,0.2), 0 0 60px rgba(255,0,128,0.06)',
    },
  },

  // ── 6. Rose Quartz ─────────────────────────────────────────────────────
  {
    id: 'rose-quartz',
    name: 'Rose Quartz',
    description: 'Soft blush canvas with dusty rose and warm copper accents',
    swatches: ['#fdf2f4', '#e11d48', '#b45309'],
    base: 'light',
    variables: {
      '--background': 'hsl(345 30% 98%)',
      '--foreground': 'hsl(345 20% 12%)',
      '--surface': 'hsl(345 22% 94%)',
      '--surface-foreground': 'hsl(345 20% 8%)',
      '--sidebar': 'hsl(345 25% 95%)',
      '--sidebar-foreground': 'hsl(345 15% 25%)',
      '--card': 'hsl(0 0% 100%)',
      '--card-foreground': 'hsl(345 20% 8%)',
      '--popup': 'hsl(0 0% 100%)',
      '--popup-foreground': 'hsl(345 20% 8%)',
      '--primary': 'hsl(345 82% 51%)',
      '--primary-foreground': 'hsl(0 0% 100%)',
      '--secondary': 'hsl(345 15% 88%)',
      '--secondary-foreground': 'hsl(345 15% 18%)',
      '--accent': 'hsl(28 90% 37%)',
      '--accent-foreground': 'hsl(0 0% 100%)',
      '--muted': 'hsl(345 12% 90%)',
      '--muted-foreground': 'hsl(345 8% 42%)',
      '--border': 'hsl(345 14% 84%)',
      '--error': 'hsl(0 72% 51%)',
      '--warning': 'hsl(38 92% 50%)',
      '--success': 'hsl(160 60% 38%)',
      '--info': 'hsl(200 70% 48%)',
      '--shadow': '0 1px 3px 0 rgba(225,29,72,0.05), 0 1px 2px -1px rgba(225,29,72,0.03)',
      '--shadow-md': '0 4px 10px -2px rgba(225,29,72,0.07), 0 2px 4px -2px rgba(225,29,72,0.04)',
      '--shadow-lg': '0 12px 28px -4px rgba(225,29,72,0.09), 0 4px 8px -4px rgba(225,29,72,0.05)',
      '--shadow-glow-primary': '0 0 24px rgba(225,29,72,0.1), 0 0 48px rgba(225,29,72,0.04)',
      '--shadow-glow-accent': '0 0 24px rgba(180,83,9,0.1), 0 0 48px rgba(180,83,9,0.04)',
    },
  },

  // ── 7. Evergreen Depth ─────────────────────────────────────────────────
  {
    id: 'evergreen-depth',
    name: 'Evergreen Depth',
    description: 'Dense forest darks with emerald greens and warm amber sparks',
    swatches: ['#0a120e', '#10b981', '#f59e0b'],
    base: 'dark',
    variables: {
      '--background': 'hsl(150 20% 4%)',
      '--foreground': 'hsl(140 10% 90%)',
      '--surface': 'hsl(150 18% 7%)',
      '--surface-foreground': 'hsl(140 10% 93%)',
      '--sidebar': 'hsl(150 18% 6%)',
      '--sidebar-foreground': 'hsl(140 8% 72%)',
      '--card': 'hsl(150 15% 10%)',
      '--card-foreground': 'hsl(140 10% 93%)',
      '--popup': 'hsl(150 15% 12%)',
      '--popup-foreground': 'hsl(140 10% 96%)',
      '--primary': 'hsl(160 84% 40%)',
      '--primary-foreground': 'hsl(0 0% 100%)',
      '--secondary': 'hsl(150 10% 18%)',
      '--secondary-foreground': 'hsl(140 8% 85%)',
      '--accent': 'hsl(42 96% 53%)',
      '--accent-foreground': 'hsl(42 10% 8%)',
      '--muted': 'hsl(150 10% 14%)',
      '--muted-foreground': 'hsl(140 5% 52%)',
      '--border': 'hsl(150 10% 20%)',
      '--error': 'hsl(0 80% 58%)',
      '--warning': 'hsl(42 96% 53%)',
      '--success': 'hsl(160 84% 40%)',
      '--info': 'hsl(200 75% 52%)',
      '--shadow': '0 1px 4px 0 rgba(0,0,0,0.5), 0 1px 2px -1px rgba(16,185,129,0.06)',
      '--shadow-md': '0 4px 12px -2px rgba(0,0,0,0.5), 0 2px 4px -2px rgba(16,185,129,0.08)',
      '--shadow-lg': '0 12px 32px -4px rgba(0,0,0,0.6), 0 4px 8px -4px rgba(16,185,129,0.1)',
      '--shadow-glow-primary': '0 0 24px rgba(16,185,129,0.18), 0 0 48px rgba(16,185,129,0.06)',
      '--shadow-glow-accent': '0 0 24px rgba(245,158,11,0.15), 0 0 48px rgba(245,158,11,0.05)',
    },
  },

  // ── 8. Slate Monolith ──────────────────────────────────────────────────
  {
    id: 'slate-monolith',
    name: 'Slate Monolith',
    description: 'Brutalist concrete greys with stark white text and red signal',
    swatches: ['#1a1a1e', '#ffffff', '#ef4444'],
    base: 'dark',
    variables: {
      '--background': 'hsl(240 5% 8%)',
      '--foreground': 'hsl(0 0% 96%)',
      '--surface': 'hsl(240 4% 11%)',
      '--surface-foreground': 'hsl(0 0% 96%)',
      '--sidebar': 'hsl(240 4% 10%)',
      '--sidebar-foreground': 'hsl(0 0% 75%)',
      '--card': 'hsl(240 4% 14%)',
      '--card-foreground': 'hsl(0 0% 96%)',
      '--popup': 'hsl(240 4% 16%)',
      '--popup-foreground': 'hsl(0 0% 98%)',
      '--primary': 'hsl(0 0% 100%)',
      '--primary-foreground': 'hsl(240 5% 8%)',
      '--secondary': 'hsl(240 3% 22%)',
      '--secondary-foreground': 'hsl(0 0% 88%)',
      '--accent': 'hsl(0 84% 60%)',
      '--accent-foreground': 'hsl(0 0% 100%)',
      '--muted': 'hsl(240 3% 16%)',
      '--muted-foreground': 'hsl(0 0% 52%)',
      '--border': 'hsl(240 3% 22%)',
      '--error': 'hsl(0 84% 60%)',
      '--warning': 'hsl(45 93% 47%)',
      '--success': 'hsl(142 71% 45%)',
      '--info': 'hsl(217 91% 60%)',
      '--shadow': '0 1px 4px 0 rgba(0,0,0,0.5), 0 1px 2px -1px rgba(0,0,0,0.4)',
      '--shadow-md': '0 4px 12px -2px rgba(0,0,0,0.6), 0 2px 4px -2px rgba(0,0,0,0.4)',
      '--shadow-lg': '0 12px 32px -4px rgba(0,0,0,0.7), 0 4px 8px -4px rgba(0,0,0,0.5)',
      '--shadow-glow-primary': '0 0 24px rgba(255,255,255,0.06), 0 0 48px rgba(255,255,255,0.02)',
      '--shadow-glow-accent': '0 0 24px rgba(239,68,68,0.18), 0 0 48px rgba(239,68,68,0.06)',
    },
  },
];

/** Look up a preset by ID */
export function getThemePreset(id: string): CustomThemePreset | undefined {
  return THEME_PRESETS.find((t) => t.id === id);
}
